import { Component, Input, OnDestroy } from '@angular/core';
import { Course, Rubric, Skill, UserSkill, UpdateUserSkillLevelPayload, RubricLevel } from '@mira-avant/mtlibs';
import { SkillService } from '../../../../services/skill.service';
import { AdminService } from '../../../../services/admin.service';
import { Subscription, combineLatest, firstValueFrom } from 'rxjs';

import { UserSkillLevelFormComponent } from '../../../forms/standard/user-skill-level-form/user-skill-level-form.component';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { RubricService } from '../../../../services/rubric.service';

@Component({
	selector: 'app-user-skill-level',
	standalone: true,
	imports: [UserSkillLevelFormComponent, NzSpinModule],
	templateUrl: './user-skill-level.component.html',
	styleUrl: './user-skill-level.component.scss',
})
export class UserSkillLevelComponent implements OnDestroy {
	@Input('course') public course: Course | undefined;
	public skills: Skill[] = [];
	public userSkills: UserSkill[] = [];
	public selectedUserCognitoUuid?: string;
	private subscriptions: Subscription = new Subscription();
	public loading: boolean = false;
	public rubric: Rubric | undefined;
	public rubricLevels: RubricLevel[] = [];

	constructor(
		public adminService: AdminService,
		public skillService: SkillService,
		private rubricService: RubricService
	) {
		// Subscribe to skills and user skills
		this.subscriptions.add(
			combineLatest([this.skillService.skills$, this.skillService.userSkills$]).subscribe(
				([skills, userSkills]) => {
					this.skills = skills;
					this.userSkills = userSkills;
				}
			)
		);

		// Subscribe to selected user
		this.subscriptions.add(
			this.adminService.selectedUser$.subscribe(user => {
				this.selectedUserCognitoUuid = user?.uuid;
				if (user?.uuid) {
					// Fetch user skill levels using uuid (treated as cognito_uuid)
					this.skillService.getUserSkillLevel$(user.uuid).catch(error => {
						console.error('Error fetching user skill levels:', error);
					});
				}
			})
		);
	}

	async ngOnChanges(): Promise<void> {
		const courseUuid = this.course?.uuid;

		if (courseUuid) {
			const rubrics: Rubric[] = await firstValueFrom(this.rubricService.rubrics$);

			this.rubric = rubrics.find(r => r.code === this.course?.rubric_code);
			if (this.rubric) {
				this.rubricLevels = await this.rubricService.getRubricLevels$(this.rubric.code);
			} else {
				console.warn('No rubric found for course:', this.course);
			}
		}
	}

	public async handleFormSubmit(event: unknown): Promise<void> {
		if (!this.selectedUserCognitoUuid || !this.course?.uuid || !this.rubric?.code) {
			console.error('Missing required inputs: user, course, or rubric');
			return;
		}

		this.loading = true;

		const formValue = event as { [key: string]: string };

		try {
			// Update all skill levels
			await Promise.all(
				Object.entries(formValue).map(([skillCode, rubricUuid]) => {
					if (!skillCode || !rubricUuid) {
						return Promise.resolve();
					}

					const rubricLevel = this.rubricLevels.find(level => level.uuid === rubricUuid);

					if (null === rubricLevel || undefined === rubricLevel) {
						return Promise.reject(new Error(`Rubric level not found for uuid: ${rubricUuid}`));
					}

					return this.skillService.updateUserSkillLevel$(this.selectedUserCognitoUuid!, skillCode, {
						rubric_level_uuid: rubricUuid,
						rubric_level_level: rubricLevel.level,
						course_uuid: this.course!.uuid,
						rubric_code: this.rubric!.code,
					} as UpdateUserSkillLevelPayload);
				})
			);
		} catch (error) {
			console.error('Error updating skill levels:', error);
		} finally {
			this.loading = false;
		}
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}
}
